import { useCallback, memo, useMemo } from 'react';
import {
  ReactHookFormAutocomplete,
  Button,
  Grid,
  Popper,
  PopperActions,
  PopperContent,
  PopperTitle,
  Typography,
  useTranslations,
  usePubSub,
  useHeroSnackbar,
} from '@uniqkey-frontend/shared-app';
import { useForm } from 'react-hook-form';
import {
  GetOrganizationsResponseModel,
  GetPartnersMovedFromForLookupResponseModel,
} from '@uniqkey-backend-partner/api-client';
import PubSubEventEnum from '../../../../enums/PubSubEventEnum';
import {
  IGetOrganizationsParams,
} from '../../../../hooks/usePartnerOrganizationsAPI/interfaces';
import { COUNTRIES_LIST } from '../../../../helpers/countries';
import usePartnerOrganizationsAPI from '../../../../hooks/usePartnerOrganizationsAPI';

export interface IOrganizationsListFilterSubmitResult {
  countryFilter: IGetOrganizationsParams['countryFilter'];
  movedFromPartner: TMovedFromPartnerOption | undefined;
  allMovedPartners: IGetOrganizationsParams['allMovedPartners'];
}

type TMovedFromPartnerOption = {
  value: GetPartnersMovedFromForLookupResponseModel['partnerId'];
  label: GetPartnersMovedFromForLookupResponseModel['partnerName'];
} | null

interface IOrganizationsListFilterProps {
  isOpen: boolean;
  anchorEl: HTMLElement | null;
  onSubmit: (result: IOrganizationsListFilterSubmitResult) => void;
  onClose: () => void;
  initialValues: IOrganizationsListFilterSubmitResult;
}

interface IFormValues {
  countryFilter: GetOrganizationsResponseModel['country'] | null;
  movedFromPartner: TMovedFromPartnerOption;
  allMovedPartners: IGetOrganizationsParams['allMovedPartners'] | null;
}

const POPPER_MIN_WIDTH = { minWidth: 500 };

const ALL_ORGANIZATIONS_VALUE = 'All moved organizations';

const OrganizationsListFilter = (props: IOrganizationsListFilterProps) => {
  const {
    isOpen, anchorEl, onSubmit, onClose, initialValues,
  } = props;
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getOrganizationsMovedFromForLookup } = usePartnerOrganizationsAPI();

  const allOrganizationsOption = useMemo(() => ({
    value: ALL_ORGANIZATIONS_VALUE,
    label: t('organizationsFilter.allOrganizations'),
  }), [t]);

  const {
    handleSubmit, control, reset, setValue, formState: { isDirty },
  } = useForm<IFormValues>({
    defaultValues: {
      countryFilter: initialValues.countryFilter ?? null,
      /*
        'movedFromPartner' is handled by 'allMovedPartners' field
        If it is true, 'movedFromPartner' should have 'allOrganizationsOption' option
      */
      movedFromPartner: initialValues.allMovedPartners
        ? allOrganizationsOption
        : (initialValues.movedFromPartner ?? null),
      allMovedPartners: initialValues.allMovedPartners ?? null,
    },
  });

  const handleOnSubmit = useCallback((value: IFormValues): void => {
    const { countryFilter, movedFromPartner } = value;
    const processedMovedFromPartner = movedFromPartner?.value === ALL_ORGANIZATIONS_VALUE
      ? undefined
      : (movedFromPartner ?? undefined); // if no value - use 'undefined' instead of 'null'
    onSubmit({
      countryFilter: countryFilter ?? undefined,
      movedFromPartner: processedMovedFromPartner,
      allMovedPartners: movedFromPartner?.value === ALL_ORGANIZATIONS_VALUE ? true : undefined,
    });
    onClose();
    reset(value); // set default values to the selected ones to reset isDirty
  }, [reset, onSubmit, onClose]);

  const handleGetOrganizationsMovedFromForLookupRequest = useCallback(async (
    searchQuery: string,
  ) => {
    try {
      const { data } = await getOrganizationsMovedFromForLookup(
        { page: 1, pageLength: 100, searchQuery },
      );
      return data;
    } catch (e) {
      showError({ text: t('common.somethingWentWrong') });
      return [];
    }
  }, [getOrganizationsMovedFromForLookup, showError, t]);

  const handleGetOrganizationsMovedFromForLookupResponseParser = useCallback((
    partners: GetPartnersMovedFromForLookupResponseModel[],
  ) => {
    const processedPartners = partners.map(
      (partner) => ({
        value: partner.partnerId,
        label: partner.partnerName,
      }),
    );
    return [allOrganizationsOption, ...processedPartners];
  }, [allOrganizationsOption]);

  const clearAll = useCallback(() => {
    setValue('countryFilter', null, { shouldDirty: true });
    setValue('movedFromPartner', null, { shouldDirty: true });
    setValue('allMovedPartners', null, { shouldDirty: true });
  }, [setValue]);

  const handleOnFadeExited = useCallback(() => {
    reset(); // reset form values when Popper is closed and animation has finished
  }, [reset]);

  const handleReset = useCallback(() => {
    reset({
      countryFilter: null,
      movedFromPartner: null,
      allMovedPartners: null,
    });
  }, [reset]);
  usePubSub(PubSubEventEnum.RESET_FILTER, handleReset);

  return (
    <Popper
      anchorEl={anchorEl}
      offsetX={-8}
      offsetY={16}
      placement="bottom-start"
      open={isOpen}
      sx={POPPER_MIN_WIDTH}
      onFadeExited={handleOnFadeExited}
    >
      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <PopperTitle onClose={onClose}>
          {t('common.filterBy')}
        </PopperTitle>
        <PopperContent>
          <Grid container direction="column" rowSpacing={2}>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('organizationsFilter.country.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  name="countryFilter"
                  placeholder={t('organizationsFilter.country.placeholder')}
                  options={COUNTRIES_LIST}
                  control={control}
                />
              </Grid>
            </Grid>
            <Grid item container alignItems="center">
              <Grid item xs={3}>
                <Typography>
                  {t('organizationsFilter.movedFromPartner.label')}
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <ReactHookFormAutocomplete
                  t={t}
                  name="movedFromPartner"
                  placeholder={t('organizationsFilter.movedFromPartner.placeholder')}
                  dataSourceRequest={handleGetOrganizationsMovedFromForLookupRequest}
                  dataSourceResponseParser={handleGetOrganizationsMovedFromForLookupResponseParser}
                  control={control}
                  fetchOnEmptyQuery
                />
              </Grid>
            </Grid>
          </Grid>
        </PopperContent>
        <PopperActions>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Button disabled={!isDirty} fullWidth type="submit">{t('common.apply')}</Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={clearAll}
              >
                {t('common.clearAll')}
              </Button>
            </Grid>
          </Grid>
        </PopperActions>
      </form>
    </Popper>
  );
};

export default memo(OrganizationsListFilter);
